import { AUTH_USERNAME_KEY, getAuthToken } from '@/lib/auth';

const DB_NAME = 'rookie-quest-keeper-offline';
const DB_VERSION = 2;
const RESPONSE_STORE = 'api-responses';
const PACK_STORE = 'campaign-packs';

const CACHEABLE_PREFIXES = [
  '/campaigns',
  '/characters',
  '/player',
  '/srd',
  '/rule-systems',
  '/progression',
  '/user-content',
  '/player-rules',
];

const CACHEABLE_EXACT = new Set([
  '/campaign-invites/joined/list',
]);

function isIndexedDbAvailable() {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}

export function getOfflineIdentityFromToken(token = '') {
  try {
    const parts = String(token).split('.');
    if (parts.length < 2) return '';
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(window.atob(padded));
    return String(payload?.sub || payload?.username || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

export function getOfflineCacheScope() {
  try {
    const token = getAuthToken();
    const tokenIdentity = token ? getOfflineIdentityFromToken(token) : '';
    if (tokenIdentity) return `user:${tokenIdentity}`;

    const username = localStorage.getItem(AUTH_USERNAME_KEY);
    if (username) return `user:${username.toLowerCase()}`;
    if (!token) return 'anonymous';

    let hash = 2166136261;
    for (let i = 0; i < token.length; i += 1) {
      hash ^= token.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return `token:${(hash >>> 0).toString(36)}`;
  } catch {
    return 'unknown';
  }
}

function normaliseUrl(config = {}) {
  const url = String(config.url || '');
  if (!url) return '';
  try {
    const parsed = new URL(url, config.baseURL || window.location.origin);
    return parsed.pathname;
  } catch {
    return url.split('?')[0];
  }
}

/** The query a URL carries inline, as pairs, in the order written. */
function inlineQueryPairs(config = {}) {
  const url = String(config.url || '');
  const marker = url.indexOf('?');
  if (marker === -1) return [];
  try {
    return [...new URLSearchParams(url.slice(marker + 1)).entries()];
  } catch {
    return [];
  }
}

function isCampaignMembersRead(path) {
  return /^\/campaign-invites\/[^/]+\/members$/.test(path);
}

export function isCacheableOfflineGet(config = {}) {
  if (String(config.method || 'get').toLowerCase() !== 'get') return false;
  const path = normaliseUrl(config);
  if (!path || path === '/auth/me' || path.startsWith('/auth/') || path.startsWith('/admin') || path.startsWith('/rook')) return false;
  return CACHEABLE_EXACT.has(path)
    || isCampaignMembersRead(path)
    || CACHEABLE_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
}

/** Every parameter of a request, inline or passed, as sorted pairs.
 *
 * Object.keys on a URLSearchParams returns nothing, so reading params that
 * way collapsed every such request onto one key and let one answer stand in
 * for another. A repeated parameter is kept as several pairs rather than one
 * value, so a filter that names two schools is not confused with one naming
 * a single school.
 */
function queryPairs(params) {
  if (!params) return [];
  if (typeof URLSearchParams !== 'undefined' && params instanceof URLSearchParams) {
    return [...params.entries()];
  }
  if (typeof params !== 'object') return [];
  return Object.keys(params).flatMap(key => (Array.isArray(params[key])
    ? params[key].map(value => [key, String(value)])
    : [[key, String(params[key])]]));
}

function stableParams(params, extraPairs = []) {
  const pairs = [...extraPairs, ...queryPairs(params)];
  if (pairs.length === 0) return '';
  const sorted = [...pairs].sort((left, right) => (
    left[0] === right[0] ? String(left[1]).localeCompare(String(right[1])) : left[0].localeCompare(right[0])
  ));
  return JSON.stringify(sorted);
}

/** The account a request was sent on behalf of.
 *
 * Read when the request goes out, not when the reply lands. The response
 * interceptor runs on arrival, and a GM who signs out mid-read and hands the
 * laptop to a player would otherwise have their campaign list filed under --
 * and later served to -- the second account.
 */
export function stampOfflineCacheScope(config = {}) {
  if (!config || config.rqkCacheScope) return config;
  return { ...config, rqkCacheScope: getOfflineCacheScope() };
}

/** Whether the account that asked for this is still the account signed in. */
export function offlineCacheScopeStillMatches(config = {}) {
  const stamped = config?.rqkCacheScope;
  if (!stamped) return true;
  return stamped === getOfflineCacheScope();
}

function scopeForConfig(config = {}) {
  return config?.rqkCacheScope || getOfflineCacheScope();
}

export function getOfflineCacheKey(config = {}) {
  return `${scopeForConfig(config)}::${normaliseUrl(config)}::${stableParams(config.params, inlineQueryPairs(config))}`;
}

function normaliseAudience(audience) {
  return audience === 'player' ? 'player' : 'gm';
}

function campaignPackKey(campaignId, audience = 'gm') {
  return `${getOfflineCacheScope()}::${normaliseAudience(audience)}:${campaignId}`;
}

function legacyCampaignPackKey(campaignId) {
  return `${getOfflineCacheScope()}::campaign:${campaignId}`;
}

function openDb() {
  if (!isIndexedDbAvailable()) return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(RESPONSE_STORE)) {
        const store = db.createObjectStore(RESPONSE_STORE, { keyPath: 'key' });
        store.createIndex('savedAt', 'savedAt');
        store.createIndex('scope', 'scope');
      } else {
        const store = request.transaction.objectStore(RESPONSE_STORE);
        if (!store.indexNames.contains('savedAt')) store.createIndex('savedAt', 'savedAt');
        if (!store.indexNames.contains('scope')) store.createIndex('scope', 'scope');
      }
      if (!db.objectStoreNames.contains(PACK_STORE)) {
        const packStore = db.createObjectStore(PACK_STORE, { keyPath: 'key' });
        packStore.createIndex('scope', 'scope');
        packStore.createIndex('campaignId', 'campaignId');
        packStore.createIndex('savedAt', 'savedAt');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function storeOfflineApiResponse(config, response) {
  if (!isCacheableOfflineGet(config) || !response || response.status < 200 || response.status >= 300) return '';
  // A reply belonging to an account that is no longer signed in is dropped
  // rather than filed. Losing one cache entry costs a slower read later;
  // filing it wrongly shows one person another person's campaign.
  if (!offlineCacheScopeStillMatches(config)) return '';
  const db = await openDb().catch(() => null);
  if (!db) return '';

  const key = getOfflineCacheKey(config);
  const record = {
    key,
    path: normaliseUrl(config),
    params: stableParams(config.params, inlineQueryPairs(config)),
    scope: scopeForConfig(config),
    data: response.data,
    status: response.status,
    savedAt: Date.now(),
  };

  await new Promise(resolve => {
    const transaction = db.transaction(RESPONSE_STORE, 'readwrite');
    transaction.objectStore(RESPONSE_STORE).put(record);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.onabort = () => resolve();
  });
  db.close();
  return key;
}

export async function readOfflineApiResponse(config) {
  if (!isCacheableOfflineGet(config)) return null;
  if (!offlineCacheScopeStillMatches(config)) return null;
  const db = await openDb().catch(() => null);
  if (!db) return null;

  const record = await new Promise(resolve => {
    const transaction = db.transaction(RESPONSE_STORE, 'readonly');
    const request = transaction.objectStore(RESPONSE_STORE).get(getOfflineCacheKey(config));
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => resolve(null);
  });
  db.close();

  if (!record) return null;
  return {
    data: record.data,
    status: 200,
    statusText: 'Offline Cache',
    headers: {
      'x-rqk-offline-cache': '1',
      'x-rqk-offline-saved-at': String(record.savedAt || ''),
    },
    config,
    rqkOffline: true,
    rqkOfflineSavedAt: record.savedAt || 0,
  };
}

export async function saveOfflineCampaignPackMetadata(pack = {}) {
  if (!pack.campaignId) return null;
  const db = await openDb().catch(() => null);
  if (!db) return null;
  const audience = normaliseAudience(pack.audience);

  const record = {
    ...pack,
    audience,
    key: campaignPackKey(pack.campaignId, audience),
    scope: getOfflineCacheScope(),
    campaignId: String(pack.campaignId),
    savedAt: Number(pack.savedAt || Date.now()),
    recordKeys: Array.from(new Set(Array.isArray(pack.recordKeys) ? pack.recordKeys.filter(Boolean) : [])),
  };

  await new Promise(resolve => {
    const transaction = db.transaction(PACK_STORE, 'readwrite');
    transaction.objectStore(PACK_STORE).put(record);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.onabort = () => resolve();
  });
  db.close();
  return record;
}

export async function getOfflineCampaignPackMetadata(campaignId, audience = 'gm') {
  if (!campaignId) return null;
  const db = await openDb().catch(() => null);
  if (!db) return null;
  const targetAudience = normaliseAudience(audience);

  const record = await new Promise(resolve => {
    const transaction = db.transaction(PACK_STORE, 'readonly');
    const store = transaction.objectStore(PACK_STORE);
    const request = store.get(campaignPackKey(campaignId, targetAudience));
    request.onsuccess = () => {
      if (request.result || targetAudience !== 'gm') {
        resolve(request.result || null);
        return;
      }
      const legacyRequest = store.get(legacyCampaignPackKey(campaignId));
      legacyRequest.onsuccess = () => resolve(legacyRequest.result ? { ...legacyRequest.result, audience: 'gm' } : null);
      legacyRequest.onerror = () => resolve(null);
    };
    request.onerror = () => resolve(null);
  });
  db.close();
  return record;
}

export async function listOfflineCampaignPacks() {
  const db = await openDb().catch(() => null);
  if (!db) return [];
  const scope = getOfflineCacheScope();
  const records = await new Promise(resolve => {
    const transaction = db.transaction(PACK_STORE, 'readonly');
    const request = transaction.objectStore(PACK_STORE).getAll();
    request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result.filter(item => item.scope === scope) : []);
    request.onerror = () => resolve([]);
  });
  db.close();
  return records
    .map(item => ({ ...item, audience: normaliseAudience(item.audience) }))
    .sort((a, b) => Number(b.savedAt || 0) - Number(a.savedAt || 0));
}

export async function removeOfflineCampaignPack(campaignId, audience = 'gm') {
  if (!campaignId) return;
  const targetAudience = normaliseAudience(audience);
  const current = await getOfflineCampaignPackMetadata(campaignId, targetAudience);
  if (!current) return;

  const allPacks = await listOfflineCampaignPacks();
  const protectedKeys = new Set(
    allPacks
      .filter(pack => pack.key !== current.key)
      .flatMap(pack => Array.isArray(pack.recordKeys) ? pack.recordKeys : [])
      .filter(Boolean)
  );
  const removableKeys = (current.recordKeys || []).filter(key => !protectedKeys.has(key));

  const db = await openDb().catch(() => null);
  if (!db) return;
  await new Promise(resolve => {
    const transaction = db.transaction([RESPONSE_STORE, PACK_STORE], 'readwrite');
    const responseStore = transaction.objectStore(RESPONSE_STORE);
    removableKeys.forEach(key => responseStore.delete(key));
    const packStore = transaction.objectStore(PACK_STORE);
    packStore.delete(campaignPackKey(campaignId, targetAudience));
    if (targetAudience === 'gm') packStore.delete(legacyCampaignPackKey(campaignId));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.onabort = () => resolve();
  });
  db.close();
}

export async function clearCurrentOfflineAccountData() {
  const scope = getOfflineCacheScope();
  if (!scope || scope === 'anonymous' || scope === 'unknown') return;
  const db = await openDb().catch(() => null);
  if (!db) return;

  await new Promise(resolve => {
    const transaction = db.transaction([RESPONSE_STORE, PACK_STORE], 'readwrite');
    const responseStore = transaction.objectStore(RESPONSE_STORE);
    const packStore = transaction.objectStore(PACK_STORE);

    const responseRequest = responseStore.getAll();
    responseRequest.onsuccess = () => {
      (responseRequest.result || []).forEach(record => {
        if (record?.scope === scope) responseStore.delete(record.key);
      });
    };

    const packRequest = packStore.getAll();
    packRequest.onsuccess = () => {
      (packRequest.result || []).forEach(record => {
        if (record?.scope === scope) packStore.delete(record.key);
      });
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.onabort = () => resolve();
  });
  db.close();
}

export async function clearOfflineApiCache() {
  const db = await openDb().catch(() => null);
  if (!db) return;
  await new Promise(resolve => {
    const stores = db.objectStoreNames.contains(PACK_STORE) ? [RESPONSE_STORE, PACK_STORE] : [RESPONSE_STORE];
    const transaction = db.transaction(stores, 'readwrite');
    transaction.objectStore(RESPONSE_STORE).clear();
    if (stores.includes(PACK_STORE)) transaction.objectStore(PACK_STORE).clear();
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.onabort = () => resolve();
  });
  db.close();
}